import { useConversation } from '@elevenlabs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AGENT_ID, XI_KEY } from '../config/livechat';
import { useWorkspace } from '../context/WorkspaceContext';
import { usePreferences } from '../context/PreferencesContext';
import { buildTalentContext } from './talentContext';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Two-tone ping (880Hz → 1320Hz, perfect fifth) signaling that voice listening
 * has started. Generated via Web Audio API, no audio file dependency. Plays
 * once per voice-session start, paired with the "Ik luister nu mee" text so
 * user gets both audio + visual confirmation.
 *
 * Browser audio policy: AudioContext requires a user gesture to start. Since
 * voice-start is always triggered by the user's mic-button click, this falls
 * within a gesture, no autoplay issues. Fails silently otherwise (some
 * browsers/devices without audio output still hit this path).
 */
function playStartPing() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const startAt = now + i * 0.12;
      const peakAt = startAt + 0.02;
      const endAt = startAt + 0.15;

      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(0.18, peakAt);
      gain.gain.exponentialRampToValueAtTime(0.001, endAt);

      osc.connect(gain).connect(ctx.destination);
      osc.start(startAt);
      osc.stop(endAt + 0.05);
    });

    // Close context after the longest oscillator finishes so we don't leak
    // audio nodes across many voice starts in one session.
    setTimeout(() => ctx.close?.().catch(() => {}), 500);
  } catch (err) {
    console.warn('[VoiceAgent] Start ping failed:', err.message);
  }
}

/**
 * useVoiceAgent. Wraps ElevenLabs useConversation with workspace integration
 *
 * Returns:
 *  - start()           Start voice session
 *  - stop()            End voice session
 *  - sendText(text)    Send text as if user spoke it (triggers agent response)
 *  - sendContext(text)  Silently inject context (no agent response)
 *  - status            'disconnected' | 'connecting' | 'connected' | 'disconnecting'
 *  - isSpeaking        Agent is currently speaking
 *  - isListening       Agent is listening for user speech
 */
export function useVoiceAgent() {
  const { state, dispatch, addMessage } = useWorkspace();
  const { persona } = usePreferences();
  // Persona-selected agent ID with safe fallback to default Job agent.
  // If the persona's agentId is undefined (env var missing for female agent),
  // we fall back to AGENT_ID so the user still gets *a* voice rather than a
  // broken session. PreferencesContext also auto-resets in that case.
  const activeAgentId = persona.agentId || AGENT_ID;
  const lastActivityRef = useRef(Date.now());
  const documentRef = useRef(state.document);
  const messagesRef = useRef(state.conversation.messages);
  const engineersRef = useRef([]);
  const [liveTranscript, setLiveTranscript] = useState({ speaker: null, text: '' });
  const [hasUserSpoken, setHasUserSpoken] = useState(false);

  // Keep refs in sync with latest state
  documentRef.current = state.document;
  messagesRef.current = state.conversation.messages;

  // Fetch talent roster once on mount; failures are silent (graceful degradation).
  useEffect(() => {
    let cancelled = false;
    async function fetchEngineers() {
      try {
        const res = await fetch(`${API_URL}/api/matching/engineers`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          engineersRef.current = data.engineers || [];
        }
      } catch {
        // Network error or parse failure — voice still works, just without talent data
      }
    }
    fetchEngineers();
    return () => { cancelled = true; };
  }, []);

  // Track whether we already created a bubble for the current AI turn
  const aiTurnActiveRef = useRef(false);
  // Typewriter state: reveal AI text word-by-word for a streaming feel
  const typewriterRef = useRef(null); // interval ID
  const fullTextRef = useRef('');     // complete text to reveal

  const stopTypewriter = useCallback(() => {
    if (typewriterRef.current) {
      clearInterval(typewriterRef.current);
      typewriterRef.current = null;
    }
    // Show any remaining text immediately
    if (fullTextRef.current && aiTurnActiveRef.current) {
      dispatch({ type: 'UPDATE_LAST_AI_MESSAGE', payload: fullTextRef.current });
    }
  }, [dispatch]);

  const startTypewriter = useCallback((text) => {
    stopTypewriter();
    fullTextRef.current = text;
    const words = text.split(/\s+/);
    if (words.length === 0) return;

    // Cap total reveal time at ~3s so text catches up to streaming audio.
    // ElevenLabs SDK onMessage delivers full text post-completion (no chunk
    // events exposed), so audio always leads, fastest typewriter that still
    // reads as "typing" keeps the visual lag minimal.
    const msPerWord = Math.max(15, Math.min(80, 3000 / words.length));
    let wordIndex = 0;

    // Show first word immediately
    const firstWord = words[0];
    if (!aiTurnActiveRef.current) {
      aiTurnActiveRef.current = true;
      addMessage('ai', firstWord);
    } else {
      dispatch({ type: 'UPDATE_LAST_AI_MESSAGE', payload: firstWord });
    }
    setLiveTranscript({ speaker: 'ai', text: firstWord });
    wordIndex = 1;

    typewriterRef.current = setInterval(() => {
      if (wordIndex >= words.length) {
        clearInterval(typewriterRef.current);
        typewriterRef.current = null;
        return;
      }
      const revealed = words.slice(0, wordIndex + 1).join(' ');
      dispatch({ type: 'UPDATE_LAST_AI_MESSAGE', payload: revealed });
      setLiveTranscript({ speaker: 'ai', text: revealed });
      wordIndex++;
    }, msPerWord);
  }, [addMessage, dispatch, stopTypewriter]);

  const convo = useConversation({
    apiKey: XI_KEY,
    agentId: activeAgentId,
    micMuted: false,
    useWakeLock: true,
    vadOptions: {
      preSpeechPadFrames: 1,
      negativeSpeechThreshold: 0.35,
      redemptionFrames: 2,
    },

    // NOTE: Do NOT use onModeChange here, the SDK silently overwrites it
    // with its own internal handler. We use useEffect on convo.isSpeaking instead.

    onMessage: (m) => {
      let role = null;
      let text = null;

      // Format B: { message: '...', source: 'user'|'ai' } (confirmed SDK v0.2.2)
      if (m.message && m.source) {
        role = m.source === 'user' ? 'user' : 'ai';
        text = m.message;
      }
      // Format A: { role: 'user'|'assistant', message: '...' }
      else if (m.role && m.message) {
        role = m.role === 'user' ? 'user' : 'ai';
        text = m.message;
      }
      // Format C: nested socket events
      else if (m.user_transcript_event?.user_transcript) {
        role = 'user';
        text = m.user_transcript_event.user_transcript;
      }
      else if (m.agent_response_event?.agent_response) {
        role = 'ai';
        text = m.agent_response_event.agent_response;
      }

      if (!text) return;

      if (role === 'user') {
        setLiveTranscript({ speaker: 'user', text });
        stopTypewriter();
        aiTurnActiveRef.current = false;
        if (text.trim().length >= 3) {
          addMessage('user', text);
          setHasUserSpoken(true);
        }
      } else {
        // AI text, start typewriter to reveal word by word
        startTypewriter(text);
      }
    },

    onError: (e) => {
      console.error('[VoiceAgent] Error:', e);
      dispatch({ type: 'SET_PROCESSING', payload: false });
    },
  });

  // React to speaking/listening changes via isSpeaking (since onModeChange is
  // overwritten by the SDK internally).
  const prevSpeakingRef = useRef(false);
  useEffect(() => {
    const speaking = convo.isSpeaking;
    if (speaking && !prevSpeakingRef.current) {
      lastActivityRef.current = Date.now();
      dispatch({ type: 'SET_LISTENING', payload: false });
    } else if (!speaking && prevSpeakingRef.current) {
      lastActivityRef.current = Date.now();
      stopTypewriter(); // ensure full text is shown when agent finishes
      aiTurnActiveRef.current = false;
      dispatch({ type: 'SET_LISTENING', payload: true });
      setLiveTranscript({ speaker: 'user', text: '' });
    }
    prevSpeakingRef.current = speaking;
  }, [convo.isSpeaking, dispatch, stopTypewriter]);

  const start = useCallback(async () => {
    // Check microphone permission
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      throw new Error('Microphone permission denied');
    }

    // Determine conversation state BEFORE starting session
    const msgs = messagesRef.current;
    const hasPriorConversation = msgs.some(m => m.sender === 'user');

    // Lazily ensure the engineer roster is loaded before the session starts.
    // If the mount-fetch already resolved, engineersRef.current is populated and
    // this is a no-op. Otherwise we fetch once now so the first session has data.
    if (engineersRef.current.length === 0) {
      try {
        const res = await fetch(`${API_URL}/api/matching/engineers`);
        if (res.ok) {
          const data = await res.json();
          engineersRef.current = data.engineers || [];
        }
      } catch {
        // Fetch failed — continue without talent data
      }
    }

    setHasUserSpoken(false);
    await convo.startSession({
      clientTools: {
        // get_talents: async tool the agent can call to fetch the full roster
        // including dossiers. Works only if the shared ElevenLabs agent config
        // declares a tool with this name; context injection below is the
        // reliable path regardless.
        get_talents: async () => {
          return JSON.stringify({ talents: engineersRef.current });
        },
      },
    });

    // Always inject the talent roster as the agent's knowledge base for this session.
    const talentContext = [
      'Je bent de Humanaize talent-matcher. Gebruik uitsluitend de onderstaande echte profielen; verzin geen kandidaten.',
      buildTalentContext(engineersRef.current),
    ].join('\n');
    convo.sendContextualUpdate?.(talentContext);

    // Inject prior text-conversation summary so the agent can continue seamlessly.
    if (hasPriorConversation) {
      const summary = msgs
        .filter(m => m.sender === 'user' || m.sender === 'ai')
        .slice(-10)
        .map(m => `${m.sender === 'user' ? 'Gebruiker' : 'Assistent'}: ${m.content}`)
        .join('\n');

      const context = [
        'CONTEXT: Eerder in dit gesprek is het volgende besproken:',
        summary,
        '\nGa DIRECT verder waar het gesprek gebleven is. Stel GEEN openingsvraag of introductie.',
      ].filter(Boolean).join('\n');

      convo.sendContextualUpdate?.(context);
    }

    // Audio + visual confirmation pair: ping chime followed by system message.
    playStartPing();

    dispatch({ type: 'START_SESSION' });
    addMessage('system', hasPriorConversation
      ? 'Spraak gestart, de AI gaat verder waar je gebleven was.'
      : 'Spraak gestart — vertel wie je zoekt.');
  }, [convo, dispatch, addMessage]);

  const stop = useCallback(async () => {
    await (convo.endSession?.() ?? convo.stopSession?.());
    setLiveTranscript({ speaker: null, text: '' });
    setHasUserSpoken(false);
    dispatch({ type: 'SET_STARTED', payload: false });
    addMessage('system', 'Spraak gestopt');
  }, [convo, dispatch, addMessage]);

  // Send text as user message, agent responds with voice
  const sendText = useCallback((text) => {
    if (!text?.trim()) return;
    convo.sendUserMessage?.(text);
    addMessage('user', text);
  }, [convo, addMessage]);

  // Send silent context, agent absorbs without responding
  const sendContext = useCallback((text) => {
    convo.sendContextualUpdate?.(text);
  }, [convo]);

  return {
    start,
    stop,
    sendText,
    sendContext,
    status: convo.status,
    isSpeaking: convo.isSpeaking,
    isListening: state.conversation.isListening,
    liveTranscript,
    hasUserSpoken,
  };
}
